import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Checkbox, Alert, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelect } from '@refinedev/antd';
import { contactService } from '../../../services/api/contactService';
import { businessPositionService } from '../../../services/api/businessPositionService';
import type { ProducerContact, ContactFullUpdateData, ContactUpdateData } from '../../../types/contact';
import { PhoneInput } from '../../../components/PhoneInput';
import { CreatableSelect } from '../../../components/CreatableSelect';

const { TextArea } = Input;

interface EditContactModalProps {
  visible: boolean;
  contact: ProducerContact;
  onClose: () => void;
  onSuccess: () => void;
  totalContacts?: number;
}

/**
 * EditContactModal Component
 * 
 * Modal for editing a producer contact.
 * Allows editing all contact fields including personal data and relationship fields.
 * 
 * Features:
 * - Pre-loaded form with current values
 * - Editable personal contact information (name, email, phones)
 * - Editable relationship fields (position, is_main_contact, is_favorite, notes)
 * - Error handling with server validation messages
 */
export const EditContactModal: React.FC<EditContactModalProps> = ({
  visible,
  contact,
  onClose,
  onSuccess,
  totalContacts = 1,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ContactFullUpdateData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isOnlyContact = totalContacts === 1;
  const isFavorite = contact.is_favorite;
  const cannotUnmarkFavorite = isOnlyContact && isFavorite;

  // Fetch business positions for the select dropdown
  const { selectProps: positionSelectProps } = useSelect({
    resource: 'business-positions',
    optionLabel: 'name',
    optionValue: 'id',
  });

  /**
   * Initialize form with current contact values
   */
  useEffect(() => {
    if (visible && contact) {
      form.setFieldsValue({
        name: contact.contact.name,
        surname: contact.contact.surname,
        email: contact.contact.email || '',
        main_phone: contact.contact.main_phone || '',
        secondary_phone: contact.contact.secondary_phone || '',
        identification_number: contact.contact.identification_number || '',
        position_id: contact.position?.id || null,
        is_main_contact: contact.is_main_contact,
        is_favorite: contact.is_favorite,
        notes: contact.notes || '',
      });
    }
  }, [visible, contact, form]);

  /**
   * Handle form submission
   * Updates both personal contact data and relationship fields via API
   */
  const handleSubmit = async (values: ContactFullUpdateData) => {
    setLoading(true);
    setError(null);

    try {
      // Update full contact via API
      await contactService.updateFullProducerContact(
        contact.id,
        contact.contact.id,
        values
      );
      
      // Reset form and close modal
      form.resetFields();
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating contact:', err);
      
      // Handle validation errors from backend
      if (err.validationErrors) {
        // Set form field errors
        const formErrors = Object.entries(err.validationErrors).map(([field, messages]) => ({
          name: field as keyof ContactFullUpdateData,
          errors: Array.isArray(messages) ? messages : [messages],
        }));
        form.setFields(formErrors);
      } else {
        setError(err.message || t('contacts.error.update'));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Custom validator to ensure at least one contact method is provided
   */
  const validateContactMethod = () => {
    const email = form.getFieldValue('email');
    const mainPhone = form.getFieldValue('main_phone');

    if (!email && !mainPhone) {
      return Promise.reject(new Error(t('contacts.error.at_least_one_contact')));
    }
    return Promise.resolve();
  };

  /**
   * Handle modal cancel
   * Resets form and closes modal
   */
  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onClose();
  };

  return (
    <Modal
      title={t('contacts.edit.title')}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={loading}
      width={600}
    >
      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}



      {/* Editable form fields */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {/* Row 1: Nombre / Apellidos */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('contacts.field.name')}
              rules={[
                {
                  required: true,
                  message: `${t('contacts.field.name')} ${t('common.required')}`,
                },
                {
                  whitespace: true,
                  message: `${t('contacts.field.name')} ${t('common.required')}`,
                },
              ]}
            >
              <Input
                placeholder={t('contacts.placeholder.name')}
                maxLength={200}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="surname"
              label={t('contacts.field.surname')}
              rules={[
                {
                  required: true,
                  message: `${t('contacts.field.surname')} ${t('common.required')}`,
                },
                {
                  whitespace: true,
                  message: `${t('contacts.field.surname')} ${t('common.required')}`,
                },
              ]}
            >
              <Input
                placeholder={t('contacts.placeholder.surname')}
                maxLength={200}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: Cargo / Email */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="position_id"
              label={t('contacts.field.position')}
            >
              <CreatableSelect
                {...positionSelectProps}
                placeholder={`${t('common.select')} ${t('contacts.field.position')}`}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onCreateNew={async (name) => {
                  const newPosition = await businessPositionService.createPosition(name);
                  return newPosition;
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label={t('contacts.field.email')}
              rules={[
                {
                  type: 'email',
                  message: t('pages.login.errors.validEmail'),
                },
                {
                  validator: validateContactMethod,
                },
              ]}
            >
              <Input
                type="email"
                placeholder={t('contacts.placeholder.email')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: Teléfono 1 / Teléfono 2 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="main_phone"
              label={t('contacts.field.main_phone')}
              rules={[
                {
                  validator: validateContactMethod,
                },
              ]}
            >
              <PhoneInput
                placeholder={t('contacts.placeholder.main_phone')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="secondary_phone"
              label={t('contacts.field.secondary_phone')}
            >
              <PhoneInput
                placeholder={t('contacts.placeholder.secondary_phone')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Favorito */}
        <Form.Item
          name="is_favorite"
          valuePropName="checked"
          style={{ marginBottom: 16 }}
          tooltip={cannotUnmarkFavorite 
            ? (t('contacts.error.cannot_unmark_only_favorite') || 'No se puede desmarcar el favorito si es el único contacto')
            : undefined
          }
        >
          <Checkbox disabled={cannotUnmarkFavorite}>
            {t('contacts.field.is_favorite')}
          </Checkbox>
        </Form.Item>

        {/* Row 5: Notas */}
        <Form.Item
          name="notes"
          label={t('contacts.field.notes')}
        >
          <TextArea
            rows={4}
            placeholder={t('contacts.placeholder.notes')}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditContactModal;
